/**
 * REPLAY sound identity.
 *
 * Interface sounds and the ambient bed are synthesised in real time with the
 * Web Audio API: no downloads, no dependencies, crisp at any sample rate.
 * Any sound (and the ambient loop) can be replaced by a file declared in
 * /audio/manifest.json — see docs/AUDIO.md.
 *
 * Rules: never autoplay before a user gesture, never stack bursts of sounds
 * while a direction is held, and fall completely silent while a game runs.
 */

export type Sfx = 'move' | 'confirm' | 'back' | 'open' | 'launch' | 'menuOpen' | 'menuClose' | 'notify' | 'success' | 'error' | 'toggle' | 'tab';

export interface AudioPrefs {
  sfx: boolean;
  ambient: boolean;
  sfxVolume: number; // 0..1
  ambientVolume: number; // 0..1
}

const PREFS_KEY = 'replay.audio';
const DEFAULTS: AudioPrefs = { sfx: true, ambient: true, sfxVolume: 0.6, ambientVolume: 0.35 };

export function loadPrefs(): AudioPrefs {
  try {
    return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(PREFS_KEY) ?? '{}') };
  } catch {
    return { ...DEFAULTS };
  }
}

type Manifest = Partial<Record<Sfx | 'ambient', string>>;

class AudioEngine {
  prefs: AudioPrefs = loadPrefs();
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private sfxBus!: GainNode;
  private ambientBus!: GainNode;
  private reverb!: ConvolverNode;
  private suppressed = false; // a game is running
  private unlocked = false;
  private lastPlayed = new Map<Sfx, number>();
  private manifest: Manifest = {};
  private buffers = new Map<string, AudioBuffer>();
  private ambient: { stop: () => void } | null = null;
  /** The ambient bed only plays inside the signed-in app (never on login/invite pages). */
  private ambientAllowed = false;
  private listening = false;
  private listeners = new Set<(p: AudioPrefs) => void>();
  /** Counters for diagnostics and automated tests. */
  readonly stats = { played: {} as Record<string, number>, throttled: 0, silenced: 0, ambientRunning: false };

  constructor() {
    // Optional custom sounds. Missing manifest = use the synthesised set.
    if (typeof fetch !== 'undefined') {
      fetch('/audio/manifest.json')
        .then((r) => (r.ok ? r.json() : {}))
        .then((m: Manifest) => (this.manifest = m ?? {}))
        .catch(() => {});
    }
  }

  // ---------- lifecycle ----------
  /** Call from a user gesture. Creates/resumes the context (autoplay policy). */
  unlock() {
    if (!this.ctx) {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      this.ctx = new Ctor({ latencyHint: 'interactive' });
      this.master = this.ctx.createGain();
      this.master.gain.value = 1;
      const comp = this.ctx.createDynamicsCompressor();
      comp.threshold.value = -18;
      comp.ratio.value = 3;
      this.master.connect(comp).connect(this.ctx.destination);
      this.sfxBus = this.ctx.createGain();
      this.ambientBus = this.ctx.createGain();
      this.ambientBus.gain.value = 0;
      this.reverb = this.ctx.createConvolver();
      this.reverb.buffer = this.impulse(2.6, 3.2);
      const wet = this.ctx.createGain();
      wet.gain.value = 0.22;
      this.sfxBus.connect(this.master);
      this.sfxBus.connect(this.reverb);
      this.reverb.connect(wet).connect(this.master);
      this.ambientBus.connect(this.master);
      // The ambient's reverb is fed from after its own bus: muting the ambient mutes its tail too.
      const ambientVerb = this.ctx.createGain();
      ambientVerb.gain.value = 0.9;
      this.ambientBus.connect(ambientVerb).connect(this.reverb);
      this.applyVolumes();
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume().catch(() => {});
    if (!this.unlocked && this.ctx.state !== 'suspended') this.unlocked = true;
    this.syncAmbient();
  }

  get ready() {
    return !!this.ctx && this.ctx.state === 'running';
  }

  onChange(fn: (p: AudioPrefs) => void) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  setPrefs(patch: Partial<AudioPrefs>) {
    this.prefs = { ...this.prefs, ...patch };
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify(this.prefs));
    } catch {
      /* storage unavailable */
    }
    this.applyVolumes();
    this.syncAmbient();
    for (const l of this.listeners) l(this.prefs);
  }

  /** Called by the signed-in app shell; login and invitation pages stay silent. */
  allowAmbient(on: boolean) {
    this.ambientAllowed = on;
    this.listen();
    this.syncAmbient();
  }

  /** Keeps every open tab consistent and quiet in the background. */
  private listen() {
    if (this.listening || typeof window === 'undefined') return;
    this.listening = true;
    // Muting in one tab mutes all of them.
    window.addEventListener('storage', (e) => {
      if (e.key !== PREFS_KEY) return;
      this.prefs = loadPrefs();
      this.applyVolumes();
      this.syncAmbient();
      for (const l of this.listeners) l(this.prefs);
    });
    // A tab in the background never plays the ambient bed.
    document.addEventListener('visibilitychange', () => this.syncAmbient());
  }

  /** While a game runs REPLAY is completely silent; restored on return. */
  suppress(on: boolean) {
    this.suppressed = on;
    this.syncAmbient();
  }

  private applyVolumes() {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    // Perceptual curve so the slider feels linear.
    this.sfxBus.gain.setTargetAtTime(this.prefs.sfx ? this.prefs.sfxVolume ** 1.6 * 0.9 : 0, t, 0.02);
  }

  private syncAmbient() {
    if (!this.ctx) return;
    const want =
      this.ambientAllowed &&
      this.prefs.ambient &&
      !this.suppressed &&
      !document.hidden &&
      this.prefs.ambientVolume > 0 &&
      this.ctx.state === 'running';
    const t = this.ctx.currentTime;
    const target = want ? this.prefs.ambientVolume ** 1.6 * 0.55 : 0;
    this.ambientBus.gain.cancelScheduledValues(t);
    this.ambientBus.gain.setValueAtTime(this.ambientBus.gain.value, t);
    this.ambientBus.gain.linearRampToValueAtTime(target, t + (want ? 2.5 : 0.8));
    if (want && !this.ambient) this.ambient = this.startAmbient();
    this.stats.ambientRunning = want;
    if (!want && this.ambient) {
      const a = this.ambient;
      this.ambient = null;
      setTimeout(() => {
        if (!this.ambient) a.stop();
        else a.stop();
      }, 1000);
    }
  }

  // ---------- sound effects ----------
  play(name: Sfx, opts: { intensity?: number } = {}) {
    if (!this.ctx || !this.prefs.sfx || this.suppressed || this.ctx.state !== 'running') {
      this.stats.silenced++;
      return;
    }
    const now = performance.now();
    // Throttle: held directions produce a gentle pulse, never a wall of sound.
    const minGap = name === 'move' ? 70 : 40;
    const last = this.lastPlayed.get(name) ?? 0;
    if (now - last < minGap) {
      this.stats.throttled++;
      return;
    }
    this.lastPlayed.set(name, now);
    this.stats.played[name] = (this.stats.played[name] ?? 0) + 1;
    const custom = this.manifest[name];
    if (custom) return void this.playFile(custom, this.sfxBus, opts.intensity ?? 1);
    const i = opts.intensity ?? 1;
    const c = this.ctx;
    const t = c.currentTime + 0.005;
    switch (name) {
      case 'move':
        this.blip(t, 1320, 1180, 0.028, 0.16 * i, 'sine', 5200);
        this.blip(t, 2640, 2400, 0.018, 0.03 * i, 'sine', 6000);
        break;
      case 'tab':
        this.blip(t, 740, 880, 0.06, 0.13, 'triangle', 3600);
        this.blip(t + 0.03, 1480, 1760, 0.05, 0.05, 'sine', 5000);
        break;
      case 'confirm':
        this.blip(t, 880, 880, 0.07, 0.16, 'sine', 4800);
        this.blip(t + 0.055, 1318.5, 1318.5, 0.11, 0.14, 'sine', 4800);
        break;
      case 'back':
        this.blip(t, 988, 880, 0.06, 0.12, 'sine', 3800);
        this.blip(t + 0.05, 659, 587, 0.09, 0.1, 'sine', 3200);
        break;
      case 'toggle':
        this.blip(t, 1046, 1046, 0.045, 0.12, 'triangle', 4200);
        break;
      case 'menuOpen':
        this.chord(t, [587.3, 880, 1174.7], 0.32, 0.07, 0.012, 2600, 5200);
        break;
      case 'menuClose':
        this.chord(t, [1174.7, 880, 587.3], 0.22, 0.06, 0.012, 4200, 1800);
        break;
      case 'open':
        this.swell(t, 0.42, 220, 0.11);
        this.blip(t + 0.08, 1318.5, 1318.5, 0.12, 0.08, 'sine', 5200);
        break;
      case 'launch':
        this.swell(t, 1.1, 110, 0.18);
        this.chord(t + 0.15, [220, 329.6, 440, 659.3], 1.2, 0.06, 0.03, 900, 5200);
        break;
      case 'notify':
        this.bell(t, 1567.98, 0.09);
        this.bell(t + 0.12, 2093, 0.07);
        break;
      case 'success':
        this.chord(t, [659.3], 0.12, 0.12, 0, 5000, 5000);
        this.chord(t + 0.08, [880], 0.12, 0.12, 0, 5000, 5000);
        this.chord(t + 0.16, [1318.5], 0.28, 0.1, 0, 5000, 5000);
        break;
      case 'error':
        this.blip(t, 233, 220, 0.12, 0.13, 'triangle', 1400);
        this.blip(t + 0.14, 220, 196, 0.16, 0.12, 'triangle', 1200);
        break;
    }
  }

  private env(g: GainNode, t: number, peak: number, attack: number, decay: number) {
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(Math.max(peak, 0.0002), t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
  }

  private blip(t: number, f0: number, f1: number, dur: number, peak: number, type: OscillatorType, cutoff: number) {
    const c = this.ctx!;
    const o = c.createOscillator();
    o.type = type;
    o.frequency.setValueAtTime(f0, t);
    o.frequency.exponentialRampToValueAtTime(f1, t + dur);
    const f = c.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = cutoff;
    const g = c.createGain();
    this.env(g, t, peak, 0.004, dur);
    o.connect(f).connect(g).connect(this.sfxBus);
    o.start(t);
    o.stop(t + dur + 0.05);
  }

  private chord(t: number, freqs: number[], dur: number, peak: number, stagger: number, cut0: number, cut1: number) {
    const c = this.ctx!;
    freqs.forEach((fr, k) => {
      const st = t + k * stagger;
      for (const det of [-4, 4]) {
        const o = c.createOscillator();
        o.type = 'sawtooth';
        o.frequency.value = fr;
        o.detune.value = det;
        const f = c.createBiquadFilter();
        f.type = 'lowpass';
        f.Q.value = 0.7;
        f.frequency.setValueAtTime(cut0, st);
        f.frequency.exponentialRampToValueAtTime(cut1, st + dur);
        const g = c.createGain();
        this.env(g, st, peak / 2, 0.012, dur);
        o.connect(f).connect(g).connect(this.sfxBus);
        o.start(st);
        o.stop(st + dur + 0.1);
      }
    });
  }

  private bell(t: number, f: number, peak: number) {
    const c = this.ctx!;
    for (const [ratio, amp] of [
      [1, 1],
      [2.76, 0.35],
      [5.4, 0.12],
    ] as const) {
      const o = c.createOscillator();
      o.type = 'sine';
      o.frequency.value = f * ratio;
      const g = c.createGain();
      this.env(g, t, peak * amp, 0.003, 0.6 / ratio + 0.15);
      o.connect(g).connect(this.sfxBus);
      o.start(t);
      o.stop(t + 1);
    }
  }

  /** Filtered noise sweep + low sine bloom: "opening" gesture. */
  private swell(t: number, dur: number, base: number, peak: number) {
    const c = this.ctx!;
    const n = c.createBufferSource();
    n.buffer = this.noise(dur + 0.2);
    const bp = c.createBiquadFilter();
    bp.type = 'bandpass';
    bp.Q.value = 1.4;
    bp.frequency.setValueAtTime(400, t);
    bp.frequency.exponentialRampToValueAtTime(5200, t + dur * 0.8);
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak * 0.5, t + dur * 0.6);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    n.connect(bp).connect(g).connect(this.sfxBus);
    n.start(t);
    n.stop(t + dur + 0.1);
    const o = c.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(base, t);
    o.frequency.exponentialRampToValueAtTime(base * 2, t + dur);
    const og = c.createGain();
    this.env(og, t, peak, dur * 0.5, dur * 0.6);
    o.connect(og).connect(this.sfxBus);
    o.start(t);
    o.stop(t + dur * 1.2);
  }

  private noiseCache: AudioBuffer | null = null;
  private noise(seconds: number) {
    const c = this.ctx!;
    if (this.noiseCache && this.noiseCache.duration >= seconds) return this.noiseCache;
    const len = Math.ceil(c.sampleRate * Math.max(seconds, 2));
    const b = c.createBuffer(1, len, c.sampleRate);
    const d = b.getChannelData(0);
    for (let k = 0; k < len; k++) d[k] = Math.random() * 2 - 1;
    this.noiseCache = b;
    return b;
  }

  private impulse(seconds: number, decay: number) {
    const c = this.ctx!;
    const len = Math.floor(c.sampleRate * seconds);
    const b = c.createBuffer(2, len, c.sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const d = b.getChannelData(ch);
      for (let k = 0; k < len; k++) d[k] = (Math.random() * 2 - 1) * (1 - k / len) ** decay;
    }
    return b;
  }

  private async loadFile(url: string) {
    let b = this.buffers.get(url);
    if (!b) {
      const res = await fetch(url);
      b = await this.ctx!.decodeAudioData(await res.arrayBuffer());
      this.buffers.set(url, b);
    }
    return b;
  }

  private playFile(url: string, bus: GainNode, gain: number) {
    void this.loadFile(url)
      .then((b) => {
        const s = this.ctx!.createBufferSource();
        s.buffer = b;
        const g = this.ctx!.createGain();
        g.gain.value = gain;
        s.connect(g).connect(bus);
        s.start();
      })
      .catch(() => {});
  }

  // ---------- ambient ----------
  /**
   * Generative ambient bed: slowly drifting dark-but-warm chords on detuned
   * pads through a breathing low-pass, plus a faint airy texture. Chords are
   * chosen by a random walk, so it never loops audibly.
   */
  private startAmbient(): { stop: () => void } {
    const c = this.ctx!;
    if (this.manifest.ambient) return this.startAmbientFile(this.manifest.ambient);
    const out = c.createGain();
    out.gain.value = 1;
    const lp = c.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 900;
    lp.Q.value = 0.3;
    const lfo = c.createOscillator();
    lfo.frequency.value = 0.035;
    const lfoAmt = c.createGain();
    lfoAmt.gain.value = 380;
    lfo.connect(lfoAmt).connect(lp.frequency);
    lfo.start();
    lp.connect(out);
    out.connect(this.ambientBus);

    // Air: very quiet band-passed noise, slowly moving.
    const air = c.createBufferSource();
    air.buffer = this.noise(4);
    air.loop = true;
    const airF = c.createBiquadFilter();
    airF.type = 'bandpass';
    airF.frequency.value = 2400;
    airF.Q.value = 0.6;
    const airLfo = c.createOscillator();
    airLfo.frequency.value = 0.021;
    const airLfoAmt = c.createGain();
    airLfoAmt.gain.value = 900;
    airLfo.connect(airLfoAmt).connect(airF.frequency);
    const airG = c.createGain();
    airG.gain.value = 0.012;
    air.connect(airF).connect(airG).connect(out);
    air.start();
    airLfo.start();

    // Voicings (Hz) — D minor-ish palette with suspended colours.
    const CHORDS = [
      [73.42, 146.83, 220.0, 261.63, 329.63],
      [65.41, 130.81, 196.0, 246.94, 293.66],
      [58.27, 116.54, 174.61, 220.0, 293.66],
      [55.0, 110.0, 164.81, 246.94, 329.63],
      [61.74, 123.47, 185.0, 220.0, 293.66],
      [69.3, 138.59, 207.65, 246.94, 329.63],
    ];
    let idx = 0;
    let alive = true;
    const voices: { stop: (t: number) => void }[] = [];
    const playChord = () => {
      if (!alive) return;
      const t = c.currentTime;
      const fade = 7;
      const len = 18 + Math.random() * 6;
      for (const v of voices.splice(0)) v.stop(t + fade);
      for (const f of CHORDS[idx]!) {
        const g = c.createGain();
        g.gain.setValueAtTime(0.0001, t);
        g.gain.linearRampToValueAtTime(f < 100 ? 0.05 : 0.026, t + fade);
        const oscs = [-7, 0, 6].map((det) => {
          const o = c.createOscillator();
          o.type = f < 100 ? 'sine' : 'triangle';
          o.frequency.value = f;
          o.detune.value = det + (Math.random() * 4 - 2);
          o.connect(g);
          o.start(t);
          return o;
        });
        g.connect(lp);
        voices.push({
          stop: (at: number) => {
            g.gain.cancelScheduledValues(c.currentTime);
            g.gain.setValueAtTime(g.gain.value, c.currentTime);
            g.gain.linearRampToValueAtTime(0.0001, at);
            for (const o of oscs) o.stop(at + 0.1);
          },
        });
      }
      // Random walk to a neighbouring voicing.
      idx = (idx + (Math.random() < 0.5 ? 1 : CHORDS.length - 1) + (Math.random() < 0.2 ? 2 : 0)) % CHORDS.length;
      timer = setTimeout(playChord, (len - fade / 2) * 1000);
    };
    let timer = setTimeout(playChord, 50);
    return {
      stop: () => {
        alive = false;
        clearTimeout(timer);
        const t = c.currentTime;
        for (const v of voices.splice(0)) v.stop(t + 0.5);
        air.stop(t + 0.6);
        airLfo.stop(t + 0.6);
        lfo.stop(t + 0.6);
        setTimeout(() => out.disconnect(), 800);
      },
    };
  }

  /** File-based ambient loop with an equal-power crossfade at the seam. */
  private startAmbientFile(url: string) {
    const c = this.ctx!;
    let alive = true;
    let current: AudioBufferSourceNode | null = null;
    let timer: ReturnType<typeof setTimeout>;
    const XF = 4;
    void this.loadFile(url).then((b) => {
      const next = () => {
        if (!alive) return;
        const t = c.currentTime;
        const s = c.createBufferSource();
        s.buffer = b;
        const g = c.createGain();
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(1, t + XF);
        g.gain.setValueAtTime(1, t + b.duration - XF);
        g.gain.linearRampToValueAtTime(0, t + b.duration);
        s.connect(g).connect(this.ambientBus);
        s.start(t);
        current = s;
        timer = setTimeout(next, (b.duration - XF) * 1000);
      };
      next();
    });
    return {
      stop: () => {
        alive = false;
        clearTimeout(timer);
        current?.stop(c.currentTime + 0.5);
      },
    };
  }
}

export const audio = new AudioEngine();
if (typeof window !== 'undefined') (window as unknown as { __replayAudio: AudioEngine }).__replayAudio = audio;
